import type { JSX } from "react"
import { useMemo } from "react"
import {
	IconFileZipOutline18 as ArchiveIcon,
	IconWaveformLinesOutline18 as AudioIcon,
	IconCalendarDaysOutline18 as CalendarIcon,
	IconCode3Outline18 as CodeIcon,
	IconFileContentOutline18 as DocumentIcon,
	IconEnvelopeContentOutline18 as EmailIcon,
	IconFileOutline18 as FileIcon,
	IconPhotoOutline18 as ImageIcon,
	IconBracketsCurlyOutline18 as JsonIcon,
	IconTableOutline18 as TableIcon,
	IconNotepadOutline18 as TextIcon,
	IconClapperboardOutline18 as VideoIcon,
} from "nucleo-ui-outline-18"

import { FILE_TYPE_MAP } from "@productlane/api/src/lib/files"
import { isAttachmentImage } from "@productlane/lib/src/attachments"

export type FileCategory =
	| "image"
	| "video"
	| "text"
	| "document"
	| "code"
	| "table"
	| "audio"
	| "json"
	| "archive"
	| "email"
	| "calendar"
	| "other"

/**
 * Hook to determine the appropriate file icon and file type flags.
 * @param fileType - The MIME type or file type string.
 * @param fileName - The name of the file, used to extract the file extension.
 * @param iconClassName - The CSS class name for the icon.
 * @returns Object containing the icon JSX and file type flags.
 */
export const useFileIcon = (
	fileType: string,
	fileName: string,
	iconClassName: string,
): {
	icon: JSX.Element
	isImageFile: boolean
	isVideoFile: boolean
	category: FileCategory
} => {
	const isImageFile = useMemo(() => isAttachmentImage(fileType), [fileType])
	const isVideoFile = useMemo(() => fileType.includes("video"), [fileType])

	const fileExtension = fileName.split(".").pop()?.toLowerCase() || ""

	const category = useMemo<FileCategory>(() => {
		if (isImageFile) return "image"
		if (isVideoFile) return "video"

		if (FILE_TYPE_MAP.text.includes(fileExtension)) return "text"
		if (FILE_TYPE_MAP.document.includes(fileExtension)) return "document"

		if (FILE_TYPE_MAP.code.includes(fileExtension)) return "code"
		if (FILE_TYPE_MAP.table.includes(fileExtension)) return "table"
		if (FILE_TYPE_MAP.audio.includes(fileExtension)) return "audio"
		if (FILE_TYPE_MAP.json.includes(fileExtension)) return "json"
		if (FILE_TYPE_MAP.archive.includes(fileExtension)) return "archive"
		if (FILE_TYPE_MAP.email.includes(fileExtension)) return "email"
		if (FILE_TYPE_MAP.calendar.includes(fileExtension)) return "calendar"

		return "other"
	}, [fileExtension, isImageFile, isVideoFile])

	const icon = useMemo(() => {
		switch (category) {
			case "image":
				return <ImageIcon className={iconClassName} />
			case "video":
				return <VideoIcon className={iconClassName} />
			case "text":
				return <TextIcon className={iconClassName} />
			case "document":
				return <DocumentIcon className={iconClassName} />
			case "code":
				return <CodeIcon className={iconClassName} />
			case "table":
				return <TableIcon className={iconClassName} />
			case "audio":
				return <AudioIcon className={iconClassName} />
			case "json":
				return <JsonIcon className={iconClassName} />
			case "archive":
				return <ArchiveIcon className={iconClassName} />
			case "email":
				return <EmailIcon className={iconClassName} />
			case "calendar":
				return <CalendarIcon className={iconClassName} />
			default:
				return <FileIcon className={iconClassName} />
		}
	}, [category, iconClassName])

	return { icon, isImageFile, isVideoFile, category }
}
